import { Injectable } from "@angular/core";
import { FormControl, FormGroup } from "@angular/forms";
import { EntityFormModel } from "@app/kernel/form/classes/entity-form-model";
import { TControlsValue } from "@app/kernel/form/types/form-controls-utils.types";
import { IMovement } from "@app/shared/fleet/movement/interfaces/movement.interface";
import { IConfirmMovementControls } from "../interfaces/confirm-movement-controls.interface";
import { IConfirmMovementInput } from "../interfaces/movement-action-input.interface";

/** Longest note the backend accepts. */
const NOTE_MAX_LENGTH = 2000;

/**
 * Form model of the section the Confirm action adds to the onboarding dialog.
 *
 * The driver is required and starts as the movement's driver; the notes and files describe this confirmation, so they
 * always start empty.
 */
@Injectable()
export class ConfirmMovementFormModel extends EntityFormModel<IMovement, IConfirmMovementInput, IConfirmMovementControls> {
  /**
   * Creates the section's form group.
   *
   * @param initialValue - Initial value of the controls.
   * @returns A strongly typed FormGroup<IConfirmMovementControls>.
   */
  protected createFormGroup(initialValue: TControlsValue<IConfirmMovementControls>): FormGroup<IConfirmMovementControls> {
    return new FormGroup<IConfirmMovementControls>({
      driver: new FormControl(initialValue.driver, { validators: this.buildValidator(() => ({ isNotEmpty: {} })) }),
      externalNote: new FormControl(initialValue.externalNote, {
        nonNullable: true,
        validators: this.buildValidator(() => ({ isLength: { options: { max: NOTE_MAX_LENGTH } } })),
      }),
      files: new FormControl(initialValue.files, { nonNullable: true }),
      note: new FormControl(initialValue.note, {
        nonNullable: true,
        validators: this.buildValidator(() => ({ isLength: { options: { max: NOTE_MAX_LENGTH } } })),
      }),
    });
  }

  /**
   * Starts from the movement's driver, with empty notes and no files.
   *
   * @param movement - The movement being confirmed.
   * @returns The form value.
   */
  entityToFormValue(movement?: Partial<IMovement> | null): TControlsValue<IConfirmMovementControls> {
    return { driver: movement?.expectedDriver ?? null, externalNote: "", files: [], note: "" };
  }

  /**
   * Converts the form value into the confirm input: trimmed notes and the files are left out when empty.
   *
   * @param formValue - The current form value.
   * @returns The confirm input.
   */
  formValueToDtoInput(formValue: TControlsValue<IConfirmMovementControls>): IConfirmMovementInput {
    const input: IConfirmMovementInput = {};
    if (formValue.driver) input.driver = formValue.driver.id;
    const note = formValue.note?.trim();
    if (note) input.note = note;
    const externalNote = formValue.externalNote?.trim();
    if (externalNote) input.externalNote = externalNote;
    if (formValue.files?.length) input.files = formValue.files;
    return input;
  }
}
